import React, {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cx from 'classnames';

import { PopupContext } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';
import { ChatMessageFormat } from 'types/enums/ChatMessageFormat';
import { ChatSticker } from 'types/interfaces/chat/ChatSticker';
import { ChatStickerPack } from 'types/interfaces/chat/ChatStickerPack';

import { useStickersAnalytics } from 'hooks/analytics/useStickersAnalytics';
import { useMedia } from 'hooks/useMedia';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { isAnySystemPopupOpenSelector } from 'store/systemPopup/selectors';

import { ReactComponent as CloseRoundedIcon } from 'assets/icons/close-rounded.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as SendMessageIcon } from 'assets/icons/send-message.svg';
import { ReactComponent as StickerIcon } from 'assets/icons/sticker.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { BaseTextarea } from 'components/base/BaseTextarea';

import { ImageInput } from '../ImageInput';
import { AssetsList } from './AssetsList';

import styles from './messageInput.module.sass';

export const MAX_MESSAGE_SYMBOLS = 1000;

type OnSendTextMessagePayload = {
  format: ChatMessageFormat.Text;
  body: string;
};

type OnSendIcebreakerMessagePayload = {
  format: ChatMessageFormat.Icebreaker;
  body: string;
};

type OnSendVideoMessagePayload = {
  format: ChatMessageFormat.Video;
  body: string;
};

type OnSendStickerMessagePayload = {
  format: ChatMessageFormat.Sticker;
  sticker: ChatSticker;
  stickerPack: ChatStickerPack;
  onSuccessPayment: () => void;
};

export type OnSendMessageParams =
  | OnSendTextMessagePayload
  | OnSendIcebreakerMessagePayload
  | OnSendStickerMessagePayload
  | OnSendVideoMessagePayload;

type PropTypes = {
  isStickerInputShown: boolean;
  isCreditsTooltipExist: boolean;
  prepend?: ReactElement;
  showPhotoLoader?: boolean;
  disabled?: boolean;
  isStickersEnabled?: boolean;
  isRestoreFocus?: boolean;
  shownStickerPackId?: number;
  wrapperClassName?: string;
  onSend: (payload: OnSendMessageParams) => void;
  onStickerInputHide?: () => void;
  onRecentStickerSent?: (sticker: ChatSticker) => void;
  selectShownStickerPackId?: (packId: number) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onToggleTooltip?: () => void;
  toggleStickerInput?: () => void;
  onImageLoaded?: (file: File) => void;
  onImageException?: () => void;
  onImageError?: (errors: string[]) => void;
};

export const MessageInput: React.FC<PropTypes> = React.memo(
  ({
    isStickerInputShown,
    isCreditsTooltipExist,
    prepend,
    showPhotoLoader,
    disabled,
    isStickersEnabled = true,
    isRestoreFocus = true,
    shownStickerPackId = -1,
    wrapperClassName,
    selectShownStickerPackId,
    onSend,
    onStickerInputHide,
    onRecentStickerSent,
    onBlur,
    onFocus,
    onToggleTooltip,
    toggleStickerInput,
    onImageLoaded = () => {},
    onImageException = () => {},
    onImageError = () => {},
  }) => {
    const { t } = useTranslation('app');

    const { isMobile } = useMedia();

    const { isAnyPopupOpen } = useContext(PopupContext);

    const isAnySystemPopupOpen = useSelector(isAnySystemPopupOpenSelector);

    const [text, setText] = useState('');

    const messageInputRef = useRef<HTMLTextAreaElement>(null);

    const assetsRef = useOutsideClick((event) => {
      const { isStickerMessage } = event.target.dataset;

      if (
        !isStickerInputShown ||
        isAnyPopupOpen() ||
        isAnySystemPopupOpen ||
        isStickerMessage
      )
        return;

      onStickerInputHide && onStickerInputHide();
    });

    const { trackClickOnLoadPhoto, trackClickOnAssetTab } =
      useStickersAnalytics();

    const isTextInputEmpty = useMemo(() => !text.trim(), [text]);

    const canSendSticker = useMemo(
      () => isStickersEnabled && !disabled,
      [disabled, isStickersEnabled]
    );

    const sendTextMessage = useCallback(() => {
      if (isTextInputEmpty) {
        return;
      }

      const isVideoMessage = text.match(/user\d+\/videos\/\d+/);

      onSend({
        format: isVideoMessage
          ? ChatMessageFormat.Video
          : ChatMessageFormat.Text,
        body: text.trim(),
      });

      setText('');

      if (isRestoreFocus) {
        messageInputRef.current?.focus();
      }
    }, [isRestoreFocus, isTextInputEmpty, onSend, text]);

    const handleStickerInput = () => {
      toggleStickerInput && toggleStickerInput();
      !isStickerInputShown && trackClickOnAssetTab({ info: 'message_sticker' });
    };

    const handleClickOnLoadPhoto = (e: { target: any; currentTarget: any }) => {
      if (e.target.name === 'file-reader') {
        trackClickOnLoadPhoto();
      }
    };

    const handleStickerSelect = useCallback(
      (payload: {
        sticker: ChatSticker;
        stickerPack: ChatStickerPack;
        onSuccessPayment: () => void;
      }) => {
        onSend({
          format: ChatMessageFormat.Sticker,
          sticker: payload.sticker,
          stickerPack: payload.stickerPack,
          onSuccessPayment: payload.onSuccessPayment,
        });
      },
      [onSend]
    );

    const handleInsertEmoji = useCallback((emoji: string) => {
      setText((prev) => `${prev}${emoji}`);
    }, []);

    const keyPressHandler = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.shiftKey && event.charCode === 13) {
          return true;
        }

        if (event.charCode === 13 && !isMobile) {
          event.preventDefault();
          sendTextMessage();

          return false;
        }

        return true;
      },
      [isMobile, sendTextMessage]
    );

    if (isMobile) {
      return (
        <div
          className={cx(
            styles.wrapper,
            styles.wrapperMobile,
            wrapperClassName,
            {
              [styles.show]: !disabled,
            }
          )}
        >
          <div className={styles.textInputWrapper}>
            {isCreditsTooltipExist && (
              <BaseButton
                type={ButtonTypes.Transparent}
                className={styles.infoBtnMobile}
                onClick={onToggleTooltip}
              >
                <InfoIcon className={styles.infoIcon} />
              </BaseButton>
            )}
            <form
              className={cx(styles.form, {
                [styles.withPrepend]: Boolean(prepend),
              })}
            >
              {canSendSticker && toggleStickerInput && (
                <BaseButton
                  type={ButtonTypes.Transparent}
                  className={styles.stickerButtonMobile}
                  disabled={disabled}
                  onClick={handleStickerInput}
                >
                  {isStickerInputShown ? <CloseRoundedIcon /> : <StickerIcon />}
                </BaseButton>
              )}

              <BaseTextarea
                value={text}
                name="message"
                minRows={1}
                maxRows={10}
                maxSymbols={MAX_MESSAGE_SYMBOLS}
                isSymbolsCounterHidden
                className={cx(styles.textarea, {
                  [styles.textareaWithLeftBtn]: canSendSticker,
                })}
                rootClassName={styles.textareaWrapper}
                placeholder={t('thread.input.placeholder')}
                ref={messageInputRef}
                disabled={disabled}
                onChange={setText}
                onKeyPress={keyPressHandler}
                onFocus={onFocus}
                onBlur={onBlur}
              />

              {showPhotoLoader &&
                isTextInputEmpty &&
                !MirrorService.isArjalozMids && (
                  <div className={styles.imageInputWrapperMobile}>
                    <div onClick={handleClickOnLoadPhoto}>
                      <ImageInput
                        onException={onImageException}
                        onError={onImageError}
                        onImageLoaded={onImageLoaded}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                )}

              {(!isTextInputEmpty ||
                !showPhotoLoader ||
                MirrorService.isArjalozMids) && (
                <BaseButton
                  type={ButtonTypes.Transparent}
                  className={styles.sendMessageBtn}
                  disabled={disabled || isTextInputEmpty}
                  onClick={sendTextMessage}
                >
                  <SendMessageIcon
                    className={cx(styles.sendMessageIcon, {
                      [styles.sendMessageIconStateDisabled]: disabled,
                      [styles.sendMessageIconThemeMeplusyou]:
                        MirrorService.isMePlusYouOrLocalsjoy,
                      [styles.sendMessageIconThemeLocalsDate]:
                        MirrorService.isLocalsDateOrLustStation,
                    })}
                  />
                </BaseButton>
              )}
            </form>
          </div>

          <div
            ref={assetsRef}
            className={cx(styles.stickers, {
              [styles.stickersExpanded]: isStickerInputShown,
            })}
          >
            {isStickerInputShown && (
              <AssetsList
                shownStickerPackId={shownStickerPackId}
                handleInsertEmoji={handleInsertEmoji}
                onChange={handleStickerSelect}
                onRecentStickerSent={onRecentStickerSent}
                selectShownStickerPackId={selectShownStickerPackId}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className={cx(styles.wrapper, styles.wrapperDesktop, wrapperClassName, {
          [styles.show]: !disabled,
        })}
      >
        <div
          className={cx(
            styles.textInputWrapper,
            styles.textInputWrapperDesktop
          )}
        >
          {canSendSticker && toggleStickerInput && (
            <BaseButton
              type={ButtonTypes.Transparent}
              className={styles.stickerButtonDesktop}
              disabled={disabled}
              onClick={handleStickerInput}
            >
              {isStickerInputShown ? <CloseRoundedIcon /> : <StickerIcon />}
            </BaseButton>
          )}

          {showPhotoLoader && !MirrorService.isArjalozMids && (
            <div className={styles.imageInputWrapper}>
              <div onClick={handleClickOnLoadPhoto}>
                <ImageInput
                  onException={onImageException}
                  onError={onImageError}
                  onImageLoaded={onImageLoaded}
                  disabled={disabled}
                />
              </div>
            </div>
          )}

          <form
            className={cx(styles.form, styles.formDesktop, {
              [styles.withPrepend]: Boolean(prepend),
            })}
          >
            {isCreditsTooltipExist && (
              <BaseButton
                type={ButtonTypes.Transparent}
                className={styles.infoBtn}
                onClick={onToggleTooltip}
              >
                <InfoIcon className={styles.infoIcon} />
              </BaseButton>
            )}

            <BaseTextarea
              value={text}
              name="message"
              minRows={1}
              maxRows={10}
              maxSymbols={MAX_MESSAGE_SYMBOLS}
              isSymbolsCounterHidden
              className={cx(styles.textarea, styles.desktopTextArea, {
                [styles.textareaWithLeftBtn]: isCreditsTooltipExist,
              })}
              rootClassName={styles.textareaWrapper}
              placeholder={t('thread.input.placeholder')}
              ref={messageInputRef}
              disabled={disabled}
              onChange={setText}
              onKeyPress={keyPressHandler}
              onFocus={onFocus}
              onBlur={onBlur}
            />

            <BaseButton
              type={ButtonTypes.Accent}
              className={styles.sendMessageDesktopBtn}
              disabled={disabled || !text}
              onClick={sendTextMessage}
            >
              Send
            </BaseButton>
          </form>
        </div>

        <div
          ref={assetsRef}
          className={cx(styles.stickers, {
            [styles.stickersExpanded]: isStickerInputShown,
          })}
        >
          {isStickerInputShown && (
            <AssetsList
              shownStickerPackId={shownStickerPackId}
              handleInsertEmoji={handleInsertEmoji}
              onChange={handleStickerSelect}
              onRecentStickerSent={onRecentStickerSent}
              selectShownStickerPackId={selectShownStickerPackId}
            />
          )}
        </div>
      </div>
    );
  }
);
